import { PayloadAction, createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { LocationParams } from '../apis/helpers';
import { FilterGroup } from '@customer/filters/types';
import { useAppSelector } from '../hooks';

export interface LocationFilterEntity extends Partial<LocationParams> {
  id: string;
}

const locationFilterAdapter = createEntityAdapter({
  selectId: (lf: LocationFilterEntity) => lf.id,
  sortComparer: (a, b) => a.id.localeCompare(b.id),
});

const locationFilterSlice = createSlice({
  name: 'locationFilter',
  initialState: locationFilterAdapter.getInitialState(),
  reducers: {
    setLocationFilter(state, action: PayloadAction<LocationFilterEntity>) {
      locationFilterAdapter.upsertOne(state, action.payload);
    },
  },
});

export const { selectById: selectLocationFilterById } = locationFilterAdapter.getSelectors(
  (state: RootState) => state.locationFilter,
);

export const { setLocationFilter } = locationFilterSlice.actions;

export default locationFilterSlice.reducer;

interface LocationFilterProps {
  filterGroup?: FilterGroup;
  includeAssetId?: boolean;
  assetId?: string;
}

export const getLocationFilterEntityId = ({
  filterGroup,
  includeAssetId = false,
  assetId,
}: LocationFilterProps): string | undefined => {
  let entityId: string | undefined = undefined;

  // The filterGroup is used as a reliable id reference for the locationFilter entities.
  if (filterGroup) {
    if (includeAssetId && typeof assetId !== 'undefined') {
      entityId = `${assetId}--${filterGroup}`;
    } else {
      entityId = filterGroup;
    }
  }

  return entityId;
};

export const useLocationFilterParams = ({
  filterGroup,
  includeAssetId = false,
  assetId,
}: LocationFilterProps): Partial<LocationParams> => {
  const entityId = getLocationFilterEntityId({
    filterGroup,
    includeAssetId,
    assetId,
  });

  const entity: LocationFilterEntity | undefined = useAppSelector(state =>
    entityId ? selectLocationFilterById(state, entityId) : undefined,
  );

  const { id, ...locationFilters } = entity ?? {};

  return locationFilters;
};
